import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Slider,
  Paper,
  Grid,
  useTheme,
  Tooltip,
  FormControlLabel,
  Switch,
  Button,
  Card,
  CardContent,
} from "@mui/material";
import { motion } from "framer-motion";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BrushIcon from "@mui/icons-material/Brush";
import WebIcon from "@mui/icons-material/Web";
import StorageIcon from "@mui/icons-material/Storage";
import SpeedIcon from "@mui/icons-material/Speed";
import SecurityIcon from "@mui/icons-material/Security";

interface FeaturePrice {
  base: number;
  perPage: number;
}

const Calculator: React.FC = () => {
  const theme = useTheme();
  const [pages, setPages] = useState<number>(5);
  const [features, setFeatures] = useState({
    ecommerce: false,
    customDevelopment: false,
    cms: false,
    hosting: false,
    performance: false,
    security: false,
  });

  const prices: Record<string, FeaturePrice> = {
    base: { base: 1500, perPage: 200 },
    ecommerce: { base: 2000, perPage: 300 },
    customDevelopment: { base: 1000, perPage: 50 },
    cms: { base: 800, perPage: 50 },
    hosting: { base: 500, perPage: 0 },
    performance: { base: 0, perPage: 80 },
    security: { base: 700, perPage: 0 },
  };

  const calculateTotal = () => {
    let total = prices.base.base + pages * prices.base.perPage;

    Object.entries(features).forEach(([key, value]) => {
      if (value && prices[key]) {
        total += prices[key].base + pages * prices[key].perPage;
      }
    });

    return total;
  };

  const handleFeatureChange = (feature: keyof typeof features) => {
    setFeatures((prev) => ({
      ...prev,
      [feature]: !prev[feature],
    }));
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            Preisrechner
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ maxWidth: "800px", mx: "auto", mb: 4 }}
          >
            Kalkulieren Sie die Kosten für Ihr individuelles Webprojekt
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: "100%",
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(255, 255, 255, 0.8)",
                backdropFilter: "blur(10px)",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  Wie viele Unterseiten benötigen Sie?
                </Typography>
                <Slider
                  value={pages}
                  onChange={(_, value) => setPages(value as number)}
                  min={1}
                  max={20}
                  marks
                  valueLabelDisplay="on"
                  sx={{
                    color: theme.palette.primary.main,
                    "& .MuiSlider-thumb": {
                      "&:hover, &.Mui-focusVisible": {
                        boxShadow: `0px 0px 0px 8px ${theme.palette.primary.main}20`,
                      },
                    },
                  }}
                />
              </Box>

              <Typography variant="h6" sx={{ mb: 3 }}>
                Wählen Sie Ihre Features
              </Typography>
              <Grid container spacing={3}>
                {[
                  {
                    key: "ecommerce",
                    label: "E-Commerce",
                    icon: <ShoppingCartIcon />,
                    tooltip: "Vollständige Online-Shop Integration",
                  },
                  {
                    key: "customDevelopment",
                    label: "Individuelle Entwicklung",
                    icon: <BrushIcon />,
                    tooltip: "Individuelle, maßgeschneiderte Entwicklung",
                  },
                  {
                    key: "cms",
                    label: "CMS",
                    icon: <WebIcon />,
                    tooltip: "Content Management System",
                  },
                  {
                    key: "hosting",
                    label: "Hosting",
                    icon: <StorageIcon />,
                    tooltip: "Professionelles Webhosting",
                  },
                  {
                    key: "performance",
                    label: "Performance",
                    icon: <SpeedIcon />,
                    tooltip: "Optimierte Ladezeiten",
                  },
                  {
                    key: "security",
                    label: "Sicherheit",
                    icon: <SecurityIcon />,
                    tooltip: "Erweiterte Sicherheitsmaßnahmen",
                  },
                ].map(({ key, label, icon, tooltip }) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Card
                      elevation={0}
                      sx={{
                        background: features[key as keyof typeof features]
                          ? `linear-gradient(45deg, ${theme.palette.primary.main}20, ${theme.palette.primary.light}20)`
                          : theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.03)"
                          : "rgba(255, 255, 255, 0.5)",
                        border: `1px solid ${
                          features[key as keyof typeof features]
                            ? theme.palette.primary.main
                            : theme.palette.divider
                        }`,
                        transition: "all 0.3s ease-in-out",
                        cursor: "pointer",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          transform: "translateY(-4px)",
                        },
                      }}
                      onClick={() =>
                        handleFeatureChange(key as keyof typeof features)
                      }
                    >
                      <CardContent
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Tooltip title={tooltip} arrow>
                          <Box
                            sx={{
                              color: features[key as keyof typeof features]
                                ? theme.palette.primary.main
                                : "text.secondary",
                            }}
                          >
                            {icon}
                          </Box>
                        </Tooltip>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: features[key as keyof typeof features]
                              ? 600
                              : 400,
                            color: features[key as keyof typeof features]
                              ? theme.palette.primary.main
                              : "text.primary",
                          }}
                        >
                          {label}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: "100%",
                background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.primary.light}10)`,
                backdropFilter: "blur(10px)",
                border: `1px solid ${theme.palette.primary.main}40`,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    color: theme.palette.primary.main,
                  }}
                >
                  Ihre Konfiguration
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {pages} {pages === 1 ? "Seite" : "Seiten"}
                  </Typography>
                  {Object.entries(features).map(
                    ([key, value]) =>
                      value && (
                        <Typography
                          key={key}
                          variant="body1"
                          sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                          }}
                        >
                          +{" "}
                          {key === "ecommerce"
                            ? "E-Commerce"
                            : key === "customDevelopment"
                            ? "Individuelle Entwicklung"
                            : key === "cms"
                            ? "CMS"
                            : key === "hosting"
                            ? "Hosting"
                            : key === "performance"
                            ? "Performance"
                            : "Sicherheit"}
                        </Typography>
                      )
                  )}
                </Box>

                <Box
                  sx={{
                    mt: 4,
                    p: 3,
                    borderRadius: 2,
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(255, 255, 255, 0.8)",
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Geschätzter Preis
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    {calculateTotal().toLocaleString("de-DE")} €
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    * Unverbindliche Preisschätzung
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    mt: 4,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    px: 4,
                    py: 1.5,
                  }}
                  href="/contact"
                >
                  Kostenloses Beratungsgespräch
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </motion.div>
    </Container>
  );
};

export default Calculator;
